// A session-scoped log of the individual field changes a manager commits each
// time they update an topic. Each Update click produces one TopicMutation per
// changed field (all sharing a single timestamp); the change-history subpage
// renders these chronologically (#418).

import type { Topic } from './types';
import { TopicLogic } from './logic';
import {
  SIGNIFICANT_TITLE_CHANGE_CHARS,
  SIGNIFICANT_BODY_CHANGE_CHARS,
} from '@/modules/config';

export type TopicMutationField =
  | 'title'
  | 'type'
  | 'datetime'
  | 'location'
  | 'body'
  | 'image';

export interface TopicMutation {
  /** Stable key: `${at}::${field}` (reducer de-dupes collisions). */
  id: string;
  /** ISO timestamp shared by every mutation committed in the same update. */
  at: string;
  field: TopicMutationField;
  label: string;
  before: string;
  after: string;
}

// Short before/after descriptors for an image change, derived purely from
// presence (computeCoreDiffs only exposes a boolean). We deliberately store these
// tiny strings — never the image data itself — so the in-memory log never
// retains base64 image bytes.
function imageMutationValues(
  before: Topic,
  after: Topic
): { before: string; after: string } {
  const beforeHas = before.imageData !== undefined;
  const afterHas = after.imageData !== undefined;
  if (!beforeHas && afterHas) {
    return { before: 'No image', after: 'Image added' };
  }
  if (beforeHas && !afterHas) {
    return { before: 'Image', after: 'Image removed' };
  }
  // Both present but different bytes (computeCoreDiffs only flags a change when they
  // differ).
  return { before: 'Image', after: 'Image replaced' };
}

/**
 * Builds the per-field mutations for a single update, stamping each with the
 * shared `at` timestamp. Reuses `TopicLogic.computeCoreDiffs` for the scalar-field diff and
 * appends an image mutation last. Returns [] when nothing tracked changed.
 * Pure/deterministic — the timestamp is injected by the caller.
 */
export function buildTopicMutations(
  before: Topic,
  after: Topic,
  at: string
): TopicMutation[] {
  const diff = TopicLogic.computeCoreDiffs(before, after);
  if (diff === null) {
    return [];
  }

  const mutations: TopicMutation[] = diff.fields.map(field => ({
    id: `${at}::${field.key}`,
    at,
    field: field.key,
    label: field.label,
    before: field.before,
    after: field.after,
  }));

  if (diff.imageChanged) {
    const { before: imgBefore, after: imgAfter } = imageMutationValues(
      before,
      after
    );
    mutations.push({
      id: `${at}::image`,
      at,
      field: 'image',
      label: 'Image',
      before: imgBefore,
      after: imgAfter,
    });
  }

  return mutations;
}

/**
 * How many characters a string edit "involved": the longer remainder after
 * stripping the longest common prefix and suffix. Cheap and intuitive —
 * "Bake Sale" → "Bake Sale & Raffle" counts the 9 appended characters —
 * without the complexity of a true edit distance.
 */
export function changedCharCount(before: string, after: string): number {
  let prefix = 0;
  const maxPrefix = Math.min(before.length, after.length);
  while (prefix < maxPrefix && before[prefix] === after[prefix]) {
    prefix += 1;
  }
  let suffix = 0;
  const maxSuffix = maxPrefix - prefix;
  while (
    suffix < maxSuffix &&
    before[before.length - 1 - suffix] === after[after.length - 1 - suffix]
  ) {
    suffix += 1;
  }
  return Math.max(
    before.length - prefix - suffix,
    after.length - prefix - suffix
  );
}

/**
 * The timestamp of the *most recent* significant mutation in the session log,
 * or null when nothing significant changed (#427). Significant means: a title
 * change involving more than SIGNIFICANT_TITLE_CHANGE_CHARS characters, a
 * body change involving more than SIGNIFICANT_BODY_CHANGE_CHARS,
 * or any date/time or location change. Drives the automatic "Share Updates (recommended)"
 * button, whose change list is headed at the returned timestamp.
 *
 * The log is appended oldest-first, so we scan from the end and return the
 * first (latest) significant match. Anchoring to the *latest* rather than the
 * earliest change keeps the backend "who hasn't heard yet" filter (`before`)
 * pinned near the sender's most recent edit. Anchoring to the earliest
 * accumulated change instead lets a long-lived session pin `before` behind a
 * later reminder blast, excluding every recipient and silently offering zero
 * update candidates (#436).
 */
export function latestSignificantChangeAt(
  mutations: readonly TopicMutation[]
): string | null {
  for (let i = mutations.length - 1; i >= 0; i -= 1) {
    const mutation = mutations[i];
    switch (mutation.field) {
      case 'title':
        if (
          changedCharCount(mutation.before, mutation.after) >
          SIGNIFICANT_TITLE_CHANGE_CHARS
        ) {
          return mutation.at;
        }
        break;
      case 'body':
        if (
          changedCharCount(mutation.before, mutation.after) >
          SIGNIFICANT_BODY_CHANGE_CHARS
        ) {
          return mutation.at;
        }
        break;
      case 'datetime':
      case 'location':
        return mutation.at;
      default:
        break;
    }
  }
  return null;
}

/**
 * Formats a mutation timestamp as a short local date/time, e.g. "Apr 21, 2:45 PM".
 * Returns a stable fallback for an unparseable input rather than "Invalid Date".
 */
export function formatMutationTimestamp(at: string): string {
  if (Number.isNaN(Date.parse(at))) {
    return 'Unknown time';
  }
  return new Date(at).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
